
import { ref, Ref, defineComponent, onMounted, nextTick, computed } from "vue";
import { api } from "@/services/Api";
import router from "@/router";
import moment, { Moment } from "moment";
import swal from "sweetalert2";
import {
  CalendarSlot,
  CalendarSlotKendo,
  CalendarSchedulerEvent,
  Slot,
} from "@/models/Calendar";
import CheckBoxInput from "@/components/CheckBoxInput.vue";
import { ProductListItem, Coupon } from "@/models/Product";
import { useI18n } from "vue-i18n";
import { ApiResult } from "@/services/ApiResult";
import { CalendarAvailabilityResponse, PriceType } from "@/models/Interfaces";
import useSchedulerExtensions from "@/modules/useSchedulerExtensions";
import _ from "lodash";
import Q from "q";
import { useStore } from "@/store";
import { PriceTypeCategory, ProductRegistrationType } from "@/models/Enums";
import CalendarSlotModal from "./CalendarSlotModal.vue";
import CalendarNewSlotModal from "./CalendarNewSlotModal.vue";
import { CalendarSlotSaveResponse } from "@/models/Interfaces";
import { DayOfWeekKendo } from '@/models/Enums';
import { VIRTUAL_SLOT_ID, FAKE_SLOT_ID_PREFIX, FAKE_SLOT_ID_ATTRIBUTE } from '@/models/Constants';

let kendoQuery = $;

const headerPlusFooterHeight = 119;

class TooltipSlotInfo {
  product = "";
  startDate: Date | string = "";
  startTime = "";
  endTime = "";
  content = "";
  visible = false;
}

interface CalendarOptions extends kendo.ui.CalendarOptions {
  animation?: {
    horizontal?: {
      effects?: string;
      reverse?: boolean;
      duration?: number;
      divisor?: number;
    };
    vertical?: {
      effects?: string;
      duration?: number;
    };
  };
}

export default defineComponent({
  props: {
    date: {
      type: String,
    },
    slotId: {
      type: Number,
    },
    productId: {
      type: Number,
    },
    view: {
      type: String,
    },
    priceId: {
      type: Number,
    },
    benefitId: {
      type: Number,
    },
    changeDate: {
      type: Boolean,
      default: false
    },
    benefitCardCode: {
      type: String,
    },
    priceTypeId: {
      type: Number,
    }
  },
  components: {
    CheckBoxInput,
    CalendarSlotModal,
    CalendarNewSlotModal,
  },
  setup(props, { emit }) {
    document.documentElement.style.setProperty('--header-height', (headerPlusFooterHeight + 29) + 'px');
    const store = useStore();
    let priceDateFrom = "";
    let priceDateTo = "";
    const serverTime = ref("");
    const modal = ref<InstanceType<typeof CalendarSlotModal>>();
    const newModal = ref<InstanceType<typeof CalendarNewSlotModal>>();
    const selectSlotMode = ref<boolean>(props.productId != undefined);
    let mountedStartDate: string | undefined = "";
    const kendoWrapper: Ref<HTMLElement | undefined> = ref();
    const calendarElement: Ref<HTMLElement | undefined> = ref();
    let refreshAvailability = false;

    const { t, locale } = useI18n();
    const isRTL = locale.value == "he-IL";

    const productsPromise = api.getRefProducts();
    const slots = ref<CalendarSlot[]>([]);
    let availabilityDataPromise: Promise<
      ApiResult<CalendarAvailabilityResponse>
    > = new Promise((yes, no) =>
      yes(new ApiResult<CalendarAvailabilityResponse>(null, undefined))
    );
    const tooltipSlotInfo = ref(new TooltipSlotInfo());
    const showCalendar = ref(true);
    const selectedDate = ref(new Date());
    const kendoSheduler = ref<kendo.ui.Scheduler>({} as any);
    const kendoCalendar = ref<kendo.ui.Calendar>({} as any);
    const localDataSource = ref<CalendarSlotKendo[]>([]);
    const products = ref<ProductListItem[]>([]);
    const selectedProducts = ref<number[]>([]);
    const productCoupon = ref<Coupon | null>(null);
    const canEditSlot = computed(() => store.getters.canEditSlot as boolean);
    const canCreateChangeOrderInPast = computed(() => store.getters.canCreateChangeOrderInPast as boolean);
    const canCreateChangeSlotInPast = computed(() => store.getters.canCreateChangeSlotInPast as boolean);

    const getCurrecentProductName = computed(() => {
      return products.value.find((x) => x.id == props.productId)?.name ?? "";
    });
    const dataSource = new kendo.data.SchedulerDataSource({
      data: [],
      schema: {
        model: {
          id: "id",
          fields: {
            id: { from: "id" },
            title: {
              from: "title",
              defaultValue: "",
              validation: { required: false },
            },
            start: { type: "date", from: "start" },
            end: { type: "date", from: "end" },
            limit: { from: "limit" },
            recurrenceId: { from: "originalSlotId" },
            recurrenceRule: { from: "recurrenceRule" },
            recurrenceException: { from: "recurrenceException" },
            type: { from: "type", defaultValue: "slot" },
            productId: { from: "productId", defaultValue: "" },
            isAllDay: { type: "boolean", from: "isAllDay" },
          },
        },
      },
    });
    const {
      getKendoRange,
      findOccurence,
      findSlotInfo,
      updateVirtualSlots,
      mergeAvailability,
    } = useSchedulerExtensions(kendoSheduler, dataSource);
    const selectedDateData = computed(() => {
      return {
        day: kendo.toString(selectedDate.value, "ddd"),
        date: kendo.toString(selectedDate.value, "dd/MM/yyyy"),
      };
    });

    function goBack() {
      if (store.state.indexTooChangeInBasketProduct != null) {
        store.state.indexTooChangeInBasketProduct = null;
      }
      router.back();
    }

    function onFilter(selectAll?: boolean) {
      if (selectAll === true) {
        selectedProducts.value = products.value.map((x) => x.id);
        if (router.currentRoute.value.query.productId) {
          router.replace({
            query: {},
          });
        }
      } else if (selectAll === false) {
        selectedProducts.value = [];
      }
      refreshAvailability = true;
      dataSource.filter({
        operator: function (task: CalendarSchedulerEvent) {
          if (!selectedProducts.value.includes(task.productId)) return false;
          return true;
        },
      });
    }

    async function loadAvailability(from = "", to = "", refresh = false) {
      if (from && to) {
        availabilityDataPromise = api.getCalendarAvailability(from, to, refresh);
      }

      const v = getKendoRange();
      const startDate = moment(from || v.start).toDate();
      const endDate = moment(to || v.end)
        .endOf("day")
        .toDate();
      const response = await availabilityDataPromise;
      if (response?.errorMessage || !response?.data) {
        swal.fire({
          icon: "error",
          text: response.errorMessage || "error",
        });
        return false;
      }

      mergeAvailability(response.data.availability, startDate, endDate);
      return true;
    }

    function reloadSlotAvailability() {
      const v = getKendoRange();
      loadAvailability(
        moment(v.start).format("YYYY-MM-DD"),
        moment(v.end).format("YYYY-MM-DD")
      );
    }

    async function loadSlots(reloadAvailability = true) {
      const slotsList = slots.value.map((x) => CalendarSlotKendo.fromSlot(x));
      dataSource.data(slotsList);
      dataSource.sync();
      if (reloadAvailability) {
        if (kendoSheduler.value.view()) {
          reloadSlotAvailability();
        } else {
          kendoSheduler.value.trigger("navigate");
        }
      } else {
        updateVirtualSlots();
      }
    }

    async function onSlotChanged(response: CalendarSlotSaveResponse) {
      if (response.updateSlots) {
        var prod = products.value.find((x) => x.id == response.productId);
        if (prod) {
          prod.productSlots = [...response.slots];
        }
        var filteredSlot = slots.value.filter(
          (x) => x.productId != response.productId
        );
        slots.value = [...filteredSlot, ...response.slots];
        if (selectSlotMode.value) {
          router.replace({
            name: "calendar",
            params: {
              productId: props.productId,
              slotId: response.slotId,
              date: props.date,
              view: props.view,
              benefitId: props.benefitId,
              priceId: props.priceId,
              benefitCardCode: props.benefitCardCode,
            },
          });
          await store.commit("updateChangetSlot", {
            productId: props.productId,
            slotId: response.originalSlotId,
            date: response.disabledDate,
            newSlotId: response.slotId,
          });
        }
        await Q.delay(100);
        await FillSlots(true);
      } else {
        const slot = slots.value.find((x) => x.id == response.slotId);
        if (slot) {
          slot.limit = response.qty;
        }
      }
      await loadSlots();
      await loadAvailability();
    }

    async function selectSlotForOrder(model: CalendarSlot) {
      await FillSlots(true);
      actionSelect(model.id, model.startDate)
    }

    const calendarSlotTemplate = function (
      item: CalendarSlotKendo & kendo.data.SchedulerEvent
    ) {
      const { startTime, slotInfo, available } = findSlotInfo(item);
      const productName =
        products.value.find((x) => x.id == item.productId)?.name || "";

      const span = moment(item.end).diff(item.start, "minutes");
      let availabilityBlock = "";
      let style = "";
      if (selectSlotMode.value && ((item.isVirtual || item.apiSlot?.isVirtual) && !store.state.createdVirtualSlotIds.includes(item.slotId))) {
        style = "style='background-color: #A2A2A2;'";
      }
      if (selectSlotMode.value && props.slotId && startTime) {
        style =
          item.apiSlot?.slotFakeId == props.slotId.toString() ||
            (props.slotId.toString() == item.slotId.toString() &&
              startTime.split("_")[0] == mountedStartDate?.replaceAll("-", ""))
            ? "style='background-color: green;'"
            : style;
      }

      if (!(item as any).tail && span >= 30) {
        const availableIcon =
          available === 0 && slotInfo.sold > 0
            ? '<div class="mb-1"><i class="icon-f icon-sold fs-18"></i></div>'
            : `<div class="item mb-1">
                            <i class="icon-f icon-circle-check fs-20"></i>
                            <span class="counter">
                                ${available == null
              ? '<i class="icon-f fs-9 icon-infinity"></i>'
              : available
            }
                            </span>
                        </div>`;
        const virtualSlotTextInfo =
          item.isVirtual || item.apiSlot?.isVirtual
            ? `<div class="w-100 text-center">${t('calendar.v3.modal.limit.order-qty')}</div>`
            : '';

        availabilityBlock = `
                        <div class="slot-info">
                          ${virtualSlotTextInfo}
                            ${availableIcon}
                            <div class="item">
                                <i class="icon-f icon-orders fs-20"></i>
                                <span class="counter">${slotInfo.sold || 0
          }</span>
                            </div>
                        </div>`;
      }
      const productRow = !selectSlotMode.value
        ? `<div class="title single-ellipsis" title="${productName}">
                            <span>${productName}</span>
                        </div>`
        : "";
      return `<div ${style} class="event-slot-template" slot-id="${item.slotId
        }" ${FAKE_SLOT_ID_ATTRIBUTE}="${item.apiSlot?.slotFakeId}" data-id="${item.id}" data-date="${startTime}">
                        ${productRow}
                        <div class="title">
                            <span>${window.kendo.toString(
          item.start,
          "HH:mm"
        )} - ${window.kendo.toString(
          item.end,
          "HH:mm"
        )}</span>
                        </div>
                        ${availabilityBlock}
                    </div>`;
    };

    const eventTemplate = function (
      item: CalendarSlotKendo & kendo.data.SchedulerEvent
    ) {
      return calendarSlotTemplate(item);
    };

    function getAvailableRowTextInTooltip(available: number | null, slotInfo: any, item: CalendarSchedulerEvent) {
      if (available === 0 && slotInfo.sold > 0) return '<div><i class="icon-f icon-sold fs-18"></i></div>';
      if (item.apiSlot?.connection != false)
        return `<i class="icon-f icon-circle-check fs-20"></i>
                    <span class="mx-2">
                    ${available == null ? '<i class="icon-f fs-9 icon-infinity"></i>' : available}
                    </span>
                    <span>${t('calendar.tooltip.available')}</span>`;
      return '';
    }

    function tooltipSlotTemplate(uid: string, item: CalendarSchedulerEvent) {
      const { slotInfo, available } = findSlotInfo(item);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const editClass = item.isVirtual || item.slotId == VIRTUAL_SLOT_ID ? "disabled" : "";
      const virtualSlotText = item.isVirtual || item.apiSlot?.isVirtual ? t('calendar.v3.modal.limit.order-qty') : '';

      const productName = products.value.find(
        (x) => x.id == item.productId
      )?.name;
      const availableIcon = getAvailableRowTextInTooltip(available, slotInfo, item);
      // available === 0 && slotInfo.sold > 0
      //   ? '<i class="icon-f icon-sold fs-18"></i>'
      //   : `<i class="icon-f icon-circle-check fs-20"></i>
      //                 <span class="mx-2">
      //                     ${available == null
      //     ? '<i class="icon-f fs-9 icon-infinity"></i>'
      //     : available
      //   }
      //                 </span>
      //                 <span>${t("calendar.tooltip.available")}</span>`;
      const selectSlotBtn = selectSlotMode.value && (!item.isVirtual || store.state.createdVirtualSlotIds.includes(item.slotId))
        ? `<span class="event-icon-text" data-action="select">
                    <i class="icon-f icon-circle-check fs-20 p-1"></i>
                    <span>${t("calendar.tooltip.select")}</span>
                </span>`
        : "";
      const actionBtns = canEditSlot.value
        ? `<div class="tooltip-buttons" data-uid="${uid}">
                <span class="event-icon-text ${editClass}" data-action="edit">
                    <i class="icon-f icon-pencil fs-20 p-1"></i>
                    <span>${t("calendar.tooltip.edit")}</span>
                </span>
                ${selectSlotBtn}
                <i class="icon-f icon-delete-cross fs-10 p-end-1 close-slot-details-btn"></i>
            </div>`
        : "";
      return `
            ${actionBtns}
            <div class="pop-up-product-name font-bold">${productName}</div>
            <div class="slot-info my-2" style="font-size: 14px">
              <div class="d-flex font-bold" style="color: #9CD330">
                    ${virtualSlotText}
                </div>
                <div class="d-flex font-bold mt-2" style="color: #9CD330">
                    ${availableIcon}
                </div>

                <div class="mt-2 d-flex font-bold" style="color: #FD7465">
                    <i class="icon-f icon-orders fs-20"></i>
                    <span class="mx-2">${item.apiSlot?.connection ? item.apiSlot?.limit : slotInfo.sold || 0}</span>
                    <span>${t('calendar.tooltip.sold')}</span>
                </div>
            </div>
            `;
    }

    function tooltip(uid: string) {
      if (!uid) return "";
      const item = findOccurence(uid);
      return tooltipSlotTemplate(uid, item);
    }

    let lastDatesRange = "";

    async function onChangeView(e: kendo.ui.SchedulerNavigateEvent) {
      kendo.ui.progress(e.sender.element, true);
      setTimeout(() => {
        selectedDate.value = e.sender.date();
        const range = getKendoRange();
        const start = moment(range.start);
        const end = moment(range.end);
        const filter = {
          from: start.format("YYYY-MM-DD"),
          to: end.format("YYYY-MM-DD"),
        };
        const datesRange = filter.from + "|" + filter.to;
        if (lastDatesRange == datesRange) {
          kendo.ui.progress(e.sender.element, false);
          return;
        }
        lastDatesRange = datesRange;

        if (router.currentRoute.value.name) {
          const parms = JSON.parse(
            JSON.stringify(router.currentRoute.value.params)
          );
          parms.date = moment(e.sender.date()).format("YYYY-MM-DD");
          parms.view = e.sender.viewName();
          router.replace({
            name: router.currentRoute.value.name,
            params: parms,
            query: router.currentRoute.value.query,
          });
        }

        loadAvailability(filter.from, filter.to).then(() => {
          kendo.ui.progress(e.sender.element, false);
        });
      }, 300);
    }

    async function tooltipAction(actionName: string, uid: string) {
      const item = findOccurence(uid);
      let slotId: number | string = item.slotId;
      const dateStart = moment(item.start).format("YYYY-MM-DD");
      if (actionName == "select") {
        if (slotId == VIRTUAL_SLOT_ID) {
          slotId = item.apiSlot?.slotFakeId ?? '';
        }
        actionSelect(slotId, dateStart);
      }
      if (actionName == "edit") {
        let editAll = true;
        if (
          (item.recurrenceId || item.recurrenceRule) &&
          ["edit", "delete"].includes(actionName)
        ) {
          const swalResult = await swal.fire({
            icon: "info",
            title: t("calendar.v3.series-edit.title"),
            customClass: {
              confirmButton:
                "save-button-wrapper popup-bookit-button my-1 px-3 col-5",
              denyButton:
                "close-button-wrapper popup-bookit-button my-1 px-3 col-5",
            },
            buttonsStyling: false,
            showDenyButton: true,
            showCloseButton: true,
            confirmButtonText: t("calendar.v3.series-edit.btn.all"),
            denyButtonText: t("calendar.v3.series-edit.btn.single"),
          });
          if (swalResult.isDismissed) {
            return;
          }
          editAll = swalResult.isConfirmed;
        }
        const slot = CalendarSlot.fromKendoSlot(item, editAll);
        modal.value?.open(slot);
      }
    }

    function onCreateSlot(e: kendo.ui.SchedulerSaveEvent) {
      e.preventDefault();
      const slot = new CalendarSlot();
      if (!props.productId) {
        return;
      }
      const product = products.value.find(x => x.id == props.productId);
      if (!product) {
        return;
      }
      // if (!product.availableProductPriceTypes.includes(store.state.order.priceType)) {
      //   return;
      // }

      const priceTypeId = props.priceTypeId ? Number(props.priceTypeId) : store.state.order.priceType;

      if (isGroupPrice(priceTypeId, store.state.priceTypes)) {
        slot.forGroups = true;
        slot.forIndividuals = false;
      } else {
        slot.forGroups = false;
        slot.forIndividuals = true;
      }

      slot.productId = Number(props.productId);

      if (!e.event) return;
      const start = moment(e.event.start);
      const minutes = start.minutes() % 15;
      if (minutes > 0) {
        start.add(minutes > 7 ? 15 - minutes : -minutes, 'minutes');
      }
      slot.startDate = start.format('YYYY-MM-DD');
      slot.startTime = start.format('HH:mm');
      slot.endTime = start.add(30, 'minutes').format('HH:mm');
      openNewSlotModal(slot);
    }

    function openNewSlotModal(slot: CalendarSlot) {
      if (slot.startDate && slot.startDate < moment().format('YYYY-MM-DD') && !canCreateChangeSlotInPast.value) {
        slot.startDate = '';
      }
      if (props.changeDate) {
        if (isGroupPrice(store.state.order.priceType, store.state.priceTypes)) {
          slot.forGroups = true;
          slot.forGroupsDisabled = true;
          slot.forIndividuals = false;
          slot.forIndividualsDisabled = false;
        } else {
          slot.forGroups = false;
          slot.forGroupsDisabled = false;
          slot.forIndividuals = true;
          slot.forIndividualsDisabled = true;
        }
      }

      newModal.value?.open(slot, false, props.changeDate);
    }

    function isGroupPrice(priceTypeId: number, priceTypes: PriceType[]) {
      if (!priceTypeId) {
        return false;
      }
      const price = priceTypes.find(x => x.id == priceTypeId);
      if (!price) {
        return false;
      }
      return (price.category & PriceTypeCategory.Group) == PriceTypeCategory.Group;
    }


    function navigateDate(dayShift: number) {
      kendoSheduler.value.date(
        moment(kendoSheduler.value.date()).add({ days: dayShift }).toDate()
      );
      kendoSheduler.value.trigger("navigate");
    }

    function getSchedullerHeight() {
      var calendarHeaderHeight = selectSlotMode.value
        ? headerPlusFooterHeight
        : 69;
      return Math.max(window.innerHeight - calendarHeaderHeight - 81, 200);
    }

    function scrollToHour(hour = 8, repeatCount = 10) {
      const sheduler = kendoSheduler.value;
      const contentDiv = sheduler.element.find("div.k-scheduler-content");
      const row = sheduler.element.find(
        "div.k-scheduler-times tr:nth-child(" + (hour * 2 + 1) + ")"
      );

      if (row.length) {
        contentDiv.scrollTop(0);

        const elementTop = row.offset()?.top || 0;
        const containerTop = contentDiv.offset()?.top || 0;

        contentDiv.scrollTop(elementTop - containerTop);
      } else if (repeatCount > 0) {
        Q.delay(25).then(() => {
          scrollToHour(hour, repeatCount - 1);
        });
        return;
      }
    }

    function filterSlotByDates(startDate: Moment, endDate: Moment) {
      const filterSlots: CalendarSlot[] = [];
      for (var x = 0; x < slots.value.length; x++) {
        const slot = slots.value[x];
        var slotStartDate = moment(slot.startDate);
        if (slotStartDate.isSame(startDate)) {
          if (
            slot.repeatEndDate != null &&
            moment(slot.repeatEndDate).isAfter(endDate)
          ) {
            slot.repeatEndDate = endDate.format("YYYY-MM-DD");
          }
          filterSlots.push(slot);
          continue;
        } else if (slotStartDate.isBefore(startDate)) {
          if (slot.repeatEndDate == null) {
            continue;
          }
          if (moment(slot.repeatEndDate).isBefore(startDate)) {
            continue;
          }
          slot.startDate = startDate.format("YYYY-MM-DD");
          if (moment(slot.repeatEndDate).isAfter(endDate)) {
            slot.repeatEndDate = endDate.format("YYYY-MM-DD");
          }
          filterSlots.push(slot);
          continue;
        } else {
          if (slot.repeatEndDate == null) {
            if (slotStartDate.isSameOrBefore(endDate)) {
              filterSlots.push(slot);
            }
            continue;
          }
          if (slotStartDate.isAfter(endDate)) {
            continue;
          }
          if (slotStartDate.isSameOrBefore(endDate)) {
            if (moment(slot.repeatEndDate).isAfter(endDate)) {
              slot.repeatEndDate = endDate.format("YYYY-MM-DD");
            }
            filterSlots.push(slot);
            continue;
          }
        }
      }

      slots.value = filterSlots;
    }

    function getMinutes(time: string) {
      const [hours, mins] = time.split(":");
      return parseInt(hours) * 60 + parseInt(mins);
    }

    async function FillSlots(update = false) {
      if (update) {
        slots.value = [];
      }
      if (!props.productId) {
        products.value.forEach((product) => {
          slots.value = [...slots.value, ...product.productSlots];
        });

        return;
      }
      var productItem = products.value.find((x) => x.id == props.productId);
      if (!productItem) {
        return;
      }
      const dateNow = moment(serverTime.value);
      var slotStartDate = moment();
      for (var i = 0; i < productItem.productSlots.length; i++) {
        const slot = productItem.productSlots[i];
        if (canCreateChangeOrderInPast.value) {
          slots.value.push(slot);
          continue;
        }
        if (slot.orderBlockMinutes == null) {
          slotStartDate = moment(slot.startDate + " " + slot.startTime).add(
            getMinutes(slot.duration),
            "minutes"
          );
        } else if (slot.orderBlockMinutes == 0) {
          slotStartDate = moment(slot.startDate + " " + slot.startTime);
        } else {
          var slotStartDate2 = moment(slot.startDate + " " + slot.startTime)
            .subtract(slot.orderBlockMinutes, "minutes")
            .format();
          slotStartDate = moment(slotStartDate2);
        }

        if (slotStartDate.isAfter(dateNow)) {
          slots.value.push(slot);
          continue;
        }
        if (slot.repeatEndDate == null) {
          continue;
        }
        var slotEndDate = moment(slot.repeatEndDate + " " + slot.startTime);
        if (slotStartDate.isBefore(dateNow) && slotEndDate.isAfter(dateNow)) {
          let slotTodayStartDate;
          if (slot.orderBlockMinutes == null) {
            slotTodayStartDate = moment(
              dateNow.format("YYYY-MM-DD") + " " + slot.startTime
            ).add(getMinutes(slot.duration), "minutes");
          } else if (slot.orderBlockMinutes == 0) {
            slotTodayStartDate = moment(
              dateNow.format("YYYY-MM-DD") + " " + slot.startTime
            );
          } else {
            var slotTodayStartDate2 = moment(
              dateNow.format("YYYY-MM-DD") + " " + slot.startTime
            )
              .subtract(slot.orderBlockMinutes, "minutes")
              .format();
            slotTodayStartDate = moment(slotTodayStartDate2);
          }

          if (slotTodayStartDate.isBefore(dateNow)) {
            slot.startDate = dateNow.clone().add(1, "day").format("YYYY-MM-DD");
          } else {
            slot.startDate = dateNow.format("YYYY-MM-DD");
          }
          slots.value.push(slot);
        }
      }
      if (props.priceId) {
        const priceStartDate = moment(priceDateFrom, moment.ISO_8601);
        const priceEndDate = moment(priceDateTo, moment.ISO_8601);

        filterSlotByDates(priceStartDate, priceEndDate);
      }
      if (productCoupon.value) {
        const couponStartDate = moment(
          productCoupon.value.validDateFrom,
          moment.ISO_8601
        );
        const couponEndDate = moment(
          productCoupon.value.validDateTo,
          moment.ISO_8601
        );
        filterSlotByDates(couponStartDate, couponEndDate);
      }
      const fakeSlots: CalendarSlot[] = await store.dispatch("getVirtualSlots");
      fakeSlots.forEach(fakeSlot => {
        slots.value.push(fakeSlot);
      });
    }

    async function actionSelect(slotId, dateStart) {
      const slot = slots.value.find((x) => x.id == slotId) ?? null;
      if (slot?.isVirtual && !store.state.createdVirtualSlotIds.includes(Number(slotId))) {
        console.log("Virtual slot cannot be selected");
        return;
      }
      if (selectSlotMode.value && props.benefitId) {
        store.commit("changeInVoucherCouponSlot", {
          productId: props.productId,
          benefitId: props.benefitId,
          slotId: slotId,
          dateStart: dateStart,
          benefitCardCode: props.benefitCardCode,
        });
        router.back();
      } else if (selectSlotMode.value) {
        if (props.priceId) {
          store.commit("changeInBasketProductSlot", {
            slotId: slotId,
            dateStart: dateStart,
          });

          router.back();
          return;
        }

        const prevSlotId = Number(router.currentRoute.value.params.slotId);
        if (prevSlotId && !store.state.order.products.find(x => x.slotId == prevSlotId)) {
          await api.withLoader().deleteCalendarSlot(prevSlotId);
        }
        router.replace({
          name: "productPriceLines",
          params: {
            productId: props.productId,
            slotId: slotId,
            dateStart: dateStart,
          },
        });
      }
    }

    function generateFakeSlotId() {
      const timestamp = Date.now();
      return `${FAKE_SLOT_ID_PREFIX}${timestamp}`;
    }

    async function addVirtualSlot(model: CalendarSlot) {
      const fakeSlotId = generateFakeSlotId();
      const slot: Slot = {
        duration: model.duration,
        endTime: model.endTime,
        id: VIRTUAL_SLOT_ID.toString(),
        quantity: 0,
        startTime: model.startTime,
        endTimeView: model.endTime,
        startTimeView: model.startTime,
        isVirtual: model.isVirtual,
        isNew: true,
        daysOfWeek: [],
        productId: model.productId,
        startDate: model.startDate.toString(),
        columnShift: 0,
        durationMinutes: 0,
        slotId: 0,
        sold: 0,
        slotFakeId: fakeSlotId,
        available: 0
      };
      store.commit("addVirtualSlot", slot);
      if (selectSlotMode.value && props.priceId) {
        actionSelect(fakeSlotId, model.startDate)
      }
      router.replace({
        name: "productPriceLines",
        params: {
          productId: props.productId,
          slotId: fakeSlotId,
          dateStart: model.startDate.toString()
        },
      });
    }

    onMounted(async () => {
      if (store.state.indexTooChangeInBasketProduct != null) {
        productCoupon.value =
          store.state.order.products[store.state.indexTooChangeInBasketProduct]
            .priceLine?.coupon ?? null;
      }
      if (props.productId && props.priceId) {
        const apiPricePromise = await api.GetProductPriceStartEndDate(
          props.priceId
        );
        if (apiPricePromise.data) {
          priceDateFrom = apiPricePromise.data.fromDate;
          priceDateTo = apiPricePromise.data.toDate;
        }
      }
      const getDateTimeResult = await api.CalendarGetDateTime();
      serverTime.value = getDateTimeResult.data ?? "";
      const schedulerHeight = getSchedullerHeight();
      mountedStartDate = props.date;
      selectedDate.value = new Date();
      if (mountedStartDate) {
        selectedDate.value = moment(mountedStartDate).toDate();
      }
      let selectedView = (props.view || "week").toLowerCase();
      if (!["day", "week"].includes(selectedView)) {
        selectedView = "week";
      }
      const calendarKendo = calendarElement.value;
      const wrapperKendo = kendoWrapper.value;
      if (!wrapperKendo || !calendarKendo) {
        console.log("Error creating calendaer");
        return;
      }
      products.value = _.orderBy(
        ((await productsPromise).data || []).filter(
          (x) =>
            x.registrationType == ProductRegistrationType.RegistrationIsRequired
        ),
        (x) => x.name
      );

      await FillSlots();
      selectedProducts.value = products.value.map((x) => x.id);
      const filterProduct =
        props.productId || router.currentRoute.value.query.productId;
      if (filterProduct) {
        selectedProducts.value = selectedProducts.value.filter(
          (x) => x == filterProduct
        );
        onFilter();
      }
      const productResources = products.value.map((x) => ({
        value: x.id,
        text: x.name,
        color: selectSlotMode.value ? "#297099" : x.color,
      }));
      let isCalendarDragging = false;

      // create Calendar from div HTML element
      kendoCalendar.value = kendoQuery(calendarKendo)
        .on("click.kendoCalendar touchend", "td:has(.k-link)", function (e) {
          if (isCalendarDragging) {
            e.stopImmediatePropagation();
            e.preventDefault();
          }
        })
        .kendoCalendar({
          footer: "",
          animation: {
            horizontal: {
              reverse: !isRTL,
            },
          },
          change: (event: any) => {
            const val = event.sender.value();
            if (kendoSheduler.value.date() != val) {
              kendoSheduler.value.date(val);
              selectedDate.value = val;
              kendoSheduler.value.trigger("navigate");
            }
          },
        } as CalendarOptions)
        .data("kendoCalendar");

      kendoQuery(calendarKendo).kendoTouch({
        enableSwipe: true,
        swipe: function (e) {
          if (!["left", "right"].includes(e.direction || "")) return;
          const goNext = isRTL ? e.direction == "right" : e.direction == "left";

          goNext
            ? kendoCalendar.value.navigateToFuture()
            : kendoCalendar.value.navigateToPast();
        },
      });

      kendoQuery(calendarKendo)
        .find(".k-calendar-view")
        .kendoTouch({
          enableSwipe: false,
          dragstart() {
            isCalendarDragging = true;
          },
          dragend() {
            Q.delay(150).then(() => {
              isCalendarDragging = false;
            });
          },
        });

      kendoSheduler.value = kendoQuery(wrapperKendo)
        .kendoScheduler({
          dateHeaderTemplate: kendo.template(`
                    <div class="k-link k-nav-day k-date-wrapper #=kendo.date.isToday(date)?'current':''#">
                        <div class="k-date-weekday">#=kendo.toString(date, 'ddd')#</div>
                        <div class="k-date-day">#=kendo.toString(date, ' d').trim()#</div>
                    </div>`),
          date: selectedDate.value,
          majorTimeHeaderTemplate: kendo.template(
            `<span class="k-time">#=kendo.toString(date, 'HH')#.00</span>`
          ),
          allDaySlot: false,
          startTime: new Date("2013/6/13 00:00 AM"),
          height: schedulerHeight,
          views: [
            {
              type: "day",
              title: t("calendar.v3.view.day"),
              selected: selectedView == "day",
              selectedDateFormat: "{1:dd/MM/yyyy}",
            },
            {
              type: "week",
              title: t("calendar.v3.view.week"),
              selected: selectedView == "week",
              selectedDateFormat: "{1:MMMM yyyy}",
            },
          ],
          workDayStart: new Date("2000/1/1 00:00"),
          workDayEnd: new Date("2000/1/1 23:59"),
          footer: false,
          workWeekStart: 1,
          workWeekEnd: 7,
          messages: {
            today: t("calendar.v3.view.today"),
          },
          dataBound: () => {
            if (refreshAvailability) {
              refreshAvailability = false;
              nextTick(async () => {
                updateVirtualSlots();
              });
            }
          },
          dataSource,
          navigate: onChangeView,
          eventTemplate: eventTemplate,
          resources: [
            {
              field: "productId",
              title: "productId",
              dataSource: productResources,
            },
            {
              field: "selected",
              title: "type",
              dataSource: [
                {
                  value: "1",
                  text: "Slot",
                  color: "red",
                },
              ],
            },
          ],
          add: onCreateSlot,
          editable: {
            create: true,
            confirmation: false,
            destroy: false,
            move: false,
            resize: false,
            update: false,
            window: false,
          },
        })
        .data("kendoScheduler") as kendo.ui.Scheduler;

      const kendoTooltip = kendoQuery(wrapperKendo)
        .kendoTooltip({
          filter: ".event-slot-template-forced",
          position: "top",
          content: (ev: any) => tooltip(ev.target.parent().attr("data-uid")),
          callout: false,
          showOn: "click",
          show: function (e) {
            // eslint-disable-next-line no-undef
            const content = (e.sender as any).content as JQuery<HTMLElement>;
            content.parent().addClass("sheduler-tooltip");
            const btns = content.find(".tooltip-buttons");
            const uid = btns.data("uid");
            btns
              .find("[data-action]")
              .off("click")
              .on("click", function () {
                const actionName = this.dataset["action"];
                if (!actionName) return;
                kendoTooltip.hide();
                tooltipAction(actionName, uid);
              });
          },
        })
        .data("kendoTooltip");

      kendoQuery("body").on("click", ".close-slot-details-btn", function () {
        kendoTooltip.hide();
      });

      kendoQuery(wrapperKendo).on("click", ".event-slot-template", function () {
        const el = kendoQuery(this);
        if (canEditSlot.value) {
          kendoTooltip.show(el);
          return;
        }
        onClockSlotTemplate(el);
      });

      kendoQuery(wrapperKendo).on(
        "dblclick",
        ".event-slot-template",
        function () {
          kendoTooltip.hide();
          const el = kendoQuery(this);
          let slotId = el.attr("slot-id");
          const slotFakeId = el.attr(FAKE_SLOT_ID_ATTRIBUTE);
          const dateStart = el.attr("data-date")?.split("_")[0];
          const dateStart2 = moment(dateStart, "YYYYMMDD").format("YYYY-MM-DD");
          if (slotId == VIRTUAL_SLOT_ID.toString() && !!slotFakeId) {
            slotId = slotFakeId
          }
          actionSelect(slotId, dateStart2);
        }
      );

      function onClockSlotTemplate(el: any) {
        if (!selectSlotMode.value) {
          kendoTooltip.show(el);
        }
        const slotId = el.attr("slot-id");
        const dateStart = el.attr("data-date")?.split("_")[0];
        const dateStart2 = moment(dateStart, "YYYYMMDD").format("YYYY-MM-DD");
        actionSelect(slotId, dateStart2);
      }

      await nextTick();

      loadSlots(true);
      kendoTooltip.show(
        kendoQuery("<i>init tooltip to compute the height</i>")
      );
      kendoTooltip.hide();
      nextTick(() => {
        scrollToHour();
      });
    });

    return {
      selectedDate,
      selectedDateData,
      localDataSource,
      kendoWrapper,
      products,
      selectedProducts,
      tooltipSlotInfo,
      headerPlusFooterHeight,
      onFilter,
      showCalendar,
      reloadSlotAvailability,
      navigateDate,
      isRTL,
      slots,
      selectSlotMode,
      calendarElement,
      goBack,
      getCurrecentProductName,
      productCoupon,
      canEditSlot,
      onSlotChanged,
      modal,
      newModal,
      mountedStartDate,
      selectSlotForOrder,
      addVirtualSlot
    };
  },
});
